import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Entity,
  EntityRelation,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { useGetDaasEntities } from '../../../hooks';
import {
  asDaaSType,
  DAAS_APPLICATION_SET_TYPE,
  DAAS_APPLICATION_TYPE,
  DAAS_PROJECT_TYPE,
  DaaSType,
} from '../../../types';
import { ScaffolderFormPaper, ScaffolderInputHeader } from '../../components';
import {
  DaasComponentPickerConfig,
  DaasComponentPickerUiOptions,
} from './types';
import {
  convertEntityToApplication,
  convertEntityToApplicationSet,
  convertEntityToProject,
} from './utils';

const useStyles = makeStyles()(theme => ({
  inputContainer: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    width: '60%',
  },
}));

type ComponentTypeOption = { value: DaaSType; label: string };

/*
 * Rendered as an `Autocomplete` rather than a `TextField select`: MUI v4 and v6
 * coexist in this app, and a v6 `Select`/`Menu` renders unstyled and inline
 * because the v4 JSS sheets win on the `Mui*` class names both majors emit.
 */
const COMPONENT_TYPE_OPTIONS: ComponentTypeOption[] = [
  { value: DAAS_APPLICATION_TYPE, label: 'Application' },
  { value: DAAS_APPLICATION_SET_TYPE, label: 'Application Set' },
  { value: DAAS_PROJECT_TYPE, label: 'Project' },
];

const entityLabel = (entity: Entity | null) =>
  entity ? entity.metadata?.title || entity.metadata?.name || '' : '';

const dependencyOf = (entity: Entity | null | undefined) =>
  entity?.relations
    ?.filter((relation: EntityRelation) => relation.type === 'dependencyOf')
    .map((relation: EntityRelation) => relation.targetRef) || [];

export const DaasComponentPicker = (
  props: FieldExtensionComponentProps<
    DaasComponentPickerConfig,
    DaasComponentPickerUiOptions
  >,
) => {
  const {
    onChange,
    schema: { title = 'DaaS Component', description = '' },
    required,
    rawErrors,
    formData,
    disabled,
    formContext,
    uiSchema,
  } = props;

  const { classes } = useStyles();

  const uiOptions = uiSchema?.['ui:options'];
  const presetComponentType = uiOptions?.componentType;
  const fillEditFormData = uiOptions?.fillEditFormData;
  const relationsValidation = uiOptions?.relationsValidation;
  // `userOwedComponents` is the published `ui:options` key - a typo the
  // DaaS templates already spell that way, so it cannot be corrected here.
  const userOwnedComponents = Boolean(uiOptions?.userOwedComponents);
  const userInSecurityGroup = Boolean(uiOptions?.userInSecurityGroup);

  // `formData.type` and the `componentType` ui:option are plain strings from
  // the template, so an unrecognised value falls back to the default tab.
  const [selectedType, setSelectedType] = useState<DaaSType>(() =>
    asDaaSType(formData?.type ?? presetComponentType),
  );
  const [selectedComponent, setSelectedComponent] = useState<Entity | null>(
    null,
  );
  const [prefilledEntityRefNotFound, setPrefilledEntityRefNotFound] =
    useState(false);
  const [invalidPrefilledEntityRef, setInvalidPrefilledEntityRef] = useState<
    string | null
  >(null);

  const { value: components = [], loading } = useGetDaasEntities(
    selectedType,
    userOwnedComponents,
    userInSecurityGroup,
  );

  const selectedTypeOption = useMemo(
    () =>
      COMPONENT_TYPE_OPTIONS.find(option => option.value === selectedType) ??
      COMPONENT_TYPE_OPTIONS[0],
    [selectedType],
  );

  const syncEntityDataToForm = useCallback(
    (entity: Entity) => {
      const targetFormData = formContext?.formData;
      if (!fillEditFormData || !targetFormData) {
        return;
      }
      switch (selectedType) {
        case DAAS_APPLICATION_TYPE: {
          const convertedData = convertEntityToApplication(entity);
          targetFormData.applicationName = convertedData.applicationName;
          targetFormData.description = convertedData.description;
          targetFormData.owner = convertedData.owner;
          targetFormData.project = convertedData.project;
          targetFormData.syncPolicy = convertedData.syncPolicy;
          targetFormData.applicationMetadata =
            convertedData.applicationMetadata;
          break;
        }
        case DAAS_APPLICATION_SET_TYPE: {
          const convertedData = convertEntityToApplicationSet(entity);
          targetFormData.applicationSetName = convertedData.applicationSetName;
          targetFormData.description = convertedData.description;
          targetFormData.owner = convertedData.owner;
          targetFormData.generators = convertedData.generators;
          targetFormData.project = convertedData.project;
          targetFormData.syncPolicy = convertedData.syncPolicy;
          targetFormData.applicationMetadata =
            convertedData.applicationMetadata;
          break;
        }
        case DAAS_PROJECT_TYPE: {
          const convertedData = convertEntityToProject(entity);
          targetFormData.projectName = convertedData.projectName;
          targetFormData.description = convertedData.description;
          targetFormData.owner = convertedData.owner;
          targetFormData.securityGroups = convertedData.securityGroups;
          targetFormData.clusters = convertedData.clusters;
          targetFormData.notificationChannel =
            convertedData.notificationChannel;
          targetFormData.notificationServices =
            convertedData.notificationServices;
          break;
        }
        default:
          break;
      }
    },
    [fillEditFormData, selectedType, formContext],
  );

  useEffect(() => {
    if (formData?.entityRef && components.length > 0 && !selectedComponent) {
      const component = components.find(
        (c: Entity) => stringifyEntityRef(c) === formData.entityRef,
      );
      if (component) {
        setPrefilledEntityRefNotFound(false);
        setInvalidPrefilledEntityRef(null);
        setSelectedComponent(component);
        onChange({
          type: selectedType,
          entityRef: stringifyEntityRef(component),
          relationsValidation: relationsValidation || false,
          dependencyOf: dependencyOf(component),
        });
        syncEntityDataToForm(component);
      } else {
        setPrefilledEntityRefNotFound(true);
        setInvalidPrefilledEntityRef(formData.entityRef);
        onChange({
          type: selectedType,
          entityRef: '',
          relationsValidation: relationsValidation || false,
          dependencyOf: [],
        });
      }
    }
  }, [
    formData?.entityRef,
    components,
    selectedComponent,
    selectedType,
    relationsValidation,
    onChange,
    syncEntityDataToForm,
  ]);

  const handleTypeChange = (value: DaaSType) => {
    setSelectedType(value);
    setSelectedComponent(null);
    setPrefilledEntityRefNotFound(false);
    setInvalidPrefilledEntityRef(null);
    onChange({
      type: value,
      entityRef: '',
      relationsValidation: relationsValidation || false,
      dependencyOf: [],
    });
  };

  const handleComponentSelect = (value: Entity | null) => {
    setSelectedComponent(value);
    setPrefilledEntityRefNotFound(false);
    setInvalidPrefilledEntityRef(null);
    onChange({
      type: selectedType,
      entityRef: value ? stringifyEntityRef(value) : '',
      relationsValidation: relationsValidation || false,
      dependencyOf: dependencyOf(value),
    });
    if (value) {
      syncEntityDataToForm(value);
    }
  };

  return (
    <Box>
      <ScaffolderFormPaper>
        <Box>
          <ScaffolderInputHeader title={title} description={description} />
          {!presetComponentType && (
            <Box className={classes.inputContainer}>
              <Autocomplete
                size="small"
                disableClearable
                options={COMPONENT_TYPE_OPTIONS}
                value={selectedTypeOption}
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(_, option) => handleTypeChange(option.value)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Component Type"
                    variant="outlined"
                    margin="none"
                    required={required}
                  />
                )}
              />
              <FormHelperText>Select the component type</FormHelperText>
            </Box>
          )}
          <Box className={classes.inputContainer}>
            <Autocomplete
              size="small"
              value={selectedComponent}
              loading={loading}
              onChange={(_, value) => handleComponentSelect(value)}
              options={components}
              disabled={
                !selectedType ||
                (!prefilledEntityRefNotFound && Boolean(disabled))
              }
              getOptionLabel={entityLabel}
              isOptionEqualToValue={(option, value) =>
                stringifyEntityRef(option) === stringifyEntityRef(value)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Component Name"
                  margin="none"
                  variant="outlined"
                  required={required}
                  placeholder={
                    selectedType ? 'Select a component' : 'Select a type first'
                  }
                  error={
                    prefilledEntityRefNotFound ||
                    (Boolean(rawErrors?.length) && !formData?.entityRef)
                  }
                  helperText={
                    prefilledEntityRefNotFound
                      ? `The component ${
                          invalidPrefilledEntityRef || ''
                        } is not available for editing. Please select another component.`
                      : undefined
                  }
                />
              )}
              renderOption={({ key, ...optionProps }, option) => (
                <li key={key} {...optionProps}>
                  <Box>
                    <Typography variant="body2" noWrap>
                      {option.metadata?.title || option.metadata?.name}
                    </Typography>
                  </Box>
                </li>
              )}
              noOptionsText={
                selectedType
                  ? 'No DaaS components found'
                  : 'Select a type first'
              }
              loadingText="Loading..."
            />
          </Box>
          {selectedComponent &&
            (selectedType === DAAS_APPLICATION_TYPE ||
              selectedType === DAAS_APPLICATION_SET_TYPE) && (
              <Box className={classes.inputContainer}>
                <Alert severity="warning">
                  {selectedType === DAAS_APPLICATION_TYPE ? (
                    <>
                      Deleting this component will also delete the Kubernetes
                      resources managed by the ArgoCD Application{' '}
                      <strong>
                        <code>{entityLabel(selectedComponent)}</code>
                      </strong>
                      .
                    </>
                  ) : (
                    <>
                      Deleting this component will also delete every Application
                      generated by the ArgoCD ApplicationSet{' '}
                      <strong>
                        <code>{entityLabel(selectedComponent)}</code>
                      </strong>
                      , along with the Kubernetes resources those Applications
                      manage.
                    </>
                  )}
                </Alert>
              </Box>
            )}
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};
